import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import AddSubscriptionFlow from '../components/AddSubscriptionFlow';
import { businessLabel } from '../utils/businessType';
import EnvBadge from '../components/ui/EnvBadge';
import { adminApi } from '../config/axios';
import { dateTimeFormat, useDocumentTitle } from '../config/utils';
import { relativeSince } from '../utils/relativeSince';

/**
 * «بانتظار التفعيل» / Awaiting activation — companies that finished signup (owner
 * verified the email) and have never had a subscription, newest first. SIGNUP_AUTO_TRIAL
 * is off by design, so each of these waits here until a package is granted.
 * The grant goes through the same confirm step as the company profile.
 */
export default function AwaitingActivation() {
  useDocumentTitle('Awaiting activation');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const highlight = params.get('company');
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [grantFor, setGrantFor] = useState(null);

  const load = useCallback(() => {
    setError(false);
    return adminApi.get('/companies/awaiting-activation')
      .then((r) => setRows(r.data?.data?.companies ?? []))
      .catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <h1 className="text-xl font-bold text-text-main">Awaiting activation <span dir="rtl" className="text-text-muted font-normal">· بانتظار التفعيل</span></h1>
        <Badge variant="gold">{rows ? rows.length : '…'}</Badge>
        <span className="ml-auto"><EnvBadge /></span>
      </div>
      <p className="text-sm text-text-muted mb-4">
        New accounts whose owner confirmed the email and that have no subscription yet. Granting a package opens their platform and emails the owner.
      </p>

      <div className="card overflow-x-auto">
        {rows === null && !error ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : error ? (
          <div className="p-6 text-sm text-danger">Could not load the list. <button className="underline" onClick={load}>Retry</button></div>
        ) : rows.length === 0 ? (
          <p className="p-6 text-sm text-text-muted" data-testid="awaiting-empty">Nobody is waiting — every verified account has a subscription.</p>
        ) : (
          <table className="w-full text-sm" data-testid="awaiting-table">
            <thead>
              <tr className="text-left text-text-muted text-xs uppercase tracking-wider">
                <th className="px-4 py-3">Company</th><th className="px-4 py-3">Owner email</th>
                <th className="px-4 py-3">Business type</th><th className="px-4 py-3">Signed up</th><th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.company_id} data-testid="awaiting-row"
                    className={`border-t border-white/[0.06] ${highlight === r.company_id ? 'bg-white/[0.05]' : ''}`}>
                  <td className="px-4 py-3">
                    <button className="font-medium text-text-main hover:underline text-left" dir="auto" onClick={() => navigate(`/companies/${r.company_id}`)}>{r.company_name}</button>
                  </td>
                  <td className="px-4 py-3" dir="ltr">{r.owner_email}</td>
                  <td className="px-4 py-3">{businessLabel(r.business_category, r.business_category_other)}</td>
                  <td className="px-4 py-3 whitespace-nowrap" title={dateTimeFormat(r.signed_up_at)}>
                    {dateTimeFormat(r.signed_up_at)} <span className="text-text-muted">· {relativeSince(r.signed_up_at)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button className="btn-primary py-1.5 text-xs" data-testid="grant-btn" onClick={() => setGrantFor(r.company_id)}>Grant package</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddSubscriptionFlow companyId={grantFor} open={!!grantFor} onClose={() => { setGrantFor(null); load(); }} onDone={load} />
    </Layout>
  );
}
